import { JsonPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { EventEmitter } from '@angular/core';
import { GlobalService } from '../../services/global.service';
import { LoginService } from '../../services/login.service';


@Component({
  selector: 'app-sidebar',
  imports: [NgFor, NgIf, NgClass, RouterLink, JsonPipe],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  standalone: true,
})
export class SidebarComponent {

  isSidebarExpanded = false;
  hiddenScrollbar = true;
  // showTooltip = false;
  scrollbar: any = 200;

  @Output() logout_clicked: any = new EventEmitter();

  // Role and permissions
  userRole: string = '';
  userPermissions: string[] = [];

  // Submenu control
  expandedSubmenuIndex: number | null = null;
  selected_submenu: any;
  is_submenu_open = false;
  submenuCloseTimeout: any;
  scrollPosition = 0;

  constructor(public router: Router, public ar: ActivatedRoute, public gs: GlobalService, public login: LoginService) {

  }

  ngOnInit() {
    this.checkCurrentUrl();

    // Filter sidebar items by user role
    const userRole = 'Instructor';
    this.sidebar = this.filterSidebarByRole(this.sidebar, userRole);

    // Open a default submenu on init
    this.openSubmenu(this.sidebar[3]?.submenu || []);

    this.is_submenu_open = false;

    // Track route changes to highlight active sidebar item
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.checkCurrentUrl();
      }
    });

    // Uncomment below if needed for future role-based permission filtering
    /*
    this.userRole = this.gs.userRole;
    this.userPermissions = this.gs.role_based_permissions[this.userRole] || [];
  
    this.filteredSidebar = this.sidebar.filter((item: any) => {
      if (!item.permissions) return false;
      return item.permissions.some((perm: string) => this.userPermissions.includes(perm));
    });
    */
  }

  /** Toggle the sidebar's expanded/collapsed state */
  toggleSidebar() {
    this.isSidebarExpanded = !this.isSidebarExpanded;
  }

  /** Handles the logic to toggle submenus open/close by index */
  toggleSubmenu(index: number): void {
    this.expandedSubmenuIndex = (this.expandedSubmenuIndex === index) ? null : index;
  }

  /** Set the selected sidebar item by label */
  selectItem(label: string): void {
    this.sidebar.forEach((item: any) => item.selected = false);
    const selectedItem = this.sidebar.find((item: any) => item.label === label);
    if (selectedItem) selectedItem.selected = true;
  }

  last_active_route: string = '';

  /** Checks current route and highlights the active item */
  checkCurrentUrl() {
    const currentUrl = this.router.url.split('?')[0];
    let anySelected = false;

    // Check with current URL
    this.sidebar.forEach((item: any) => {
      item.selected = this.hasActiveLink(item, currentUrl);
      if (item.selected) {
        anySelected = true;
      }
    });


    // If found, update last active
    if (anySelected) {
      this.last_active_route = currentUrl;
    }
    // If not found, fallback to last active
    else if (this.last_active_route) {
      this.sidebar.forEach((item: any) => {
        item.selected = this.hasActiveLink(item, this.last_active_route);
      });
    }
  }

  /** Recursively check if an item or its children match the active URL */
  hasActiveLink(item: any, url: string): boolean {
    // Check direct link (router_link for top-level, link for submenu items)
    if (item.router_link === url || item.link === url || (item?.queryParams && JSON.stringify(item?.queryParams) == JSON.stringify(this.ar.snapshot.queryParams))) {
      return true;
    }

    // Check nested submenu
    if (item.submenu && item.submenu.length > 0) {
      return item.submenu.some((sub: any) => this.hasActiveLink(sub, url));
    }

    return false;
  }

  /** Filter sidebar items recursively based on user role */
  filterSidebarByRole(sidebar: any[], userRole: string): any[] {
    return sidebar
      .filter(item => !item.roles || item.roles.includes(userRole))
      .map(item => {
        if (item.submenu?.length) {
          item.submenu = this.filterSidebarByRole(item.submenu, userRole);
        }
        return item;
      });
  }

  submenu_header: string = '';
  submenuTop: number = 0;

  /** Open a submenu and show it */
  openSubmenu(item: any, event?: MouseEvent) {
    this.cancelSubmenuClose();

    if (event) {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      // Adjust top position to align nicely, ensuring it doesn't go off-screen if needed
      this.submenuTop = rect.top;
    }

    if (item.submenu && item.submenu.length > 0) {
      this.selected_submenu = item.submenu;
      this.submenu_header = item.label;
      this.is_submenu_open = true;
      this.hiddenScrollbar = true;
    } else {
      this.is_submenu_open = false;
    }
  }

  /** Reopen submenu and cancel any close timeout */
  reOpenSubmenu() {
    clearTimeout(this.submenuCloseTimeout);
    this.is_submenu_open = true;
  }

  /** Close submenu after a delay (e.g., on mouse leave) */
  closeSubmenuWithDelay() {
    this.submenuCloseTimeout = setTimeout(() => {
      this.is_submenu_open = false;
      this.expandedSubmenuIndex = null
    }, 800);
  }

  /** Toggle expansion of a subgroup */
  toggleGroup(item: any, event: MouseEvent) {
    event.stopPropagation();

    // If we are opening this item, close all others first
    if (!item.expanded) {
      this.selected_submenu.forEach((sub: any) => {
        if (sub !== item) {
          sub.expanded = false;
        }
      });
    }

    item.expanded = !item.expanded;
  }

  /** Cancel the delayed submenu closing */
  cancelSubmenuClose() {
    clearTimeout(this.submenuCloseTimeout);
  }

  /** Emit logout event and clear local storage */
  logout_emitter() {
    this.logout_clicked.emit(true);
    localStorage.clear();
  }

  sidebar: any = [
    {
      id: 1,
      label: 'Dashboard',
      image: '../../../assets/Dashbaord.png',
      selected_image: '../../../assets/Dashboard-selected.png',
      selected: false,
      router_link: '/dashboard',
      submenu: [],
      permissions: this.gs.user_role_data?.dashboard_permission,
    },
    {
      id: 2,
      label: 'Masters',
      image: '../../../assets/Masters.png',
      selected_image: '../../../assets/Masters_selected.png',
      selected: false,
      permissions: this.gs.user_role_data?.masters_permission,
      submenu: [
        {
          label: 'Location Master',
          expanded: false,
          permissions: this.gs.user_role_data?.masters_location_view,
          submenu: [
            { label: 'Country', link: '/country/list', permissions: this.gs.user_role_data?.masters_location_view },
            { label: 'State', link: '/state/list', permissions: this.gs.user_role_data?.masters_location_view },
            { label: 'District', link: '/district/list', permissions: this.gs.user_role_data?.masters_location_view },
            { label: 'City', link: '/city/list', permissions: this.gs.user_role_data?.masters_location_view }
          ],
        },
        {
          label: 'School & School Branch',
          expanded: false,
          permissions: this.gs.user_role_data?.masters_university_view || this.gs.user_role_data?.masters_college_view,
          submenu: [
            { label: 'School', link: '/university/list', permissions: this.gs.user_role_data?.masters_university_view },
            { label: 'School Branch', link: '/college/list', permissions: this.gs.user_role_data?.masters_college_view }
          ],
        },
        {
          label: 'Course',
          expanded: false,
          permissions: this.gs.user_role_data?.masters_course_view,
          submenu: [
            { label: 'Level', link: '/level/list', permissions: this.gs.user_role_data?.masters_course_view },
            { label: 'Stream', link: '/stream/list', permissions: this.gs.user_role_data?.masters_course_view },
            { label: 'Program', link: '/program/list', permissions: this.gs.user_role_data?.masters_course_view },
            { label: 'Subject', link: '/subject/list', permissions: this.gs.user_role_data?.masters_course_view }
          ]
        },
      ],
    },
    {
      id: 5,
      label: 'Evaluation Management',
      image: '../../../assets/Evaluation-Management.png',
      selected_image: '../../../assets/Evaluation-Management-selected.png',
      selected: false,
      permissions: this.gs.user_role_data?.evaluation_management_permission,
      submenu: [
        { label: 'Academic year', link: '/academic-year/list', permissions: this.gs.user_role_data?.evaluation_management_academic_year_view },
        { label: 'Exam', link: '/exam/list', permissions: this.gs.user_role_data?.evaluation_management_examinations_view },
        { label: 'Assessment', link: '/assessment/list', permissions: this.gs.user_role_data?.evaluation_management_assessments_view },
      ]
    },
    {
      id: 3,
      label: 'User Management',
      image: '../../../assets/user-management.png',
      selected_image: '../../../assets/user-management-selected.png',
      selected: false,
      permissions: this.gs.user_role_data?.user_management_permission,
      submenu: [
        { label: 'Roles', link: '/role/list', permissions: this.gs.user_role_data?.user_management_rbac_view },
        { label: 'Faculty', link: '/user/list', permissions: this.gs.user_role_data?.user_management_faculty_view },
        { label: 'Student', link: '/user/student-list', permissions: this.gs.user_role_data?.user_management_students_view },
        { label: 'Student Mapping', link: '/student-mapping/list', permissions: true },
        { label: 'Head Evaluator Mapping', link: '/head-evaluator-mapping/list', permissions: this.gs.user_role_data?.assignments_assign_evaluation_view },
        // { label: 'Evaluator Mapping', link: '/evaluator-mapping/list', permissions: this.gs.user_role_data?.evaluations_and_revaluations_evaluation_view },
        // { label: 'Head Evaluator Mapping', link: '/head-evaluator-mapping/list', permissions: this.gs.user_role_data?.evaluations_and_revaluations_head_evaluation_view },
      ]
    },
    {
      id: 6,
      label: 'Script Management',
      image: '../../../assets/Script-Management.png',
      selected_image: '../../../assets/Script-Management-selected.png',
      selected: false,
      permissions: this.gs.user_role_data?.script_management_permission,
      submenu: [
        { label: 'Scripts', link: '/add-script/list', permissions: this.gs.user_role_data?.script_management_scripts_view },
        { label: 'Barcode Mapping', link: '/barcode-mapping/list', permissions: this.gs.user_role_data?.script_management_barcode_mapping_view },
      ]
    },
    {
      id: 4,
      label: 'Assignments',
      image: '../../../assets/Assignment.png',
      selected_image: '../../../assets/Assignment-selected.png',
      selected: false,
      permissions: this.gs.user_role_data?.assignments_permission,
      submenu: [
        { label: 'Assign Evaluator', link: '/evaluator-mapping/list', permissions: this.gs.user_role_data?.assignments_assign_evaluation_view },
      ]
    },
    {
      id: 7,
      label: 'Exam Results',
      image: '../../../assets/Exam-Results.png',
      selected_image: '../../../assets/Exam-Results-selected.png',
      selected: false,
      permissions: this.gs.user_role_data?.exam_results_permission,
      submenu: [
        { label: 'Evaluation Result', link: '/exam-result/list', permissions: this.gs.user_role_data?.exam_results_exam_results_view },
        { label: 'Re-evaluation Result', link: '/exam-result/list', permissions: this.gs.user_role_data?.exam_results_exam_results_view },
      ]
    },
    {
      id: 8,
      label: 'Exam Results',
      image: '../../../assets/Exam-Results2.png',
      selected_image: '../../../assets/Exam-Results2-selected.png',
      selected: false,
      router_link: '/student-portal/list',
      permissions: this.gs.user_data?.role == 'Student',
    },
    {
      id: 9,
      label: 'Re-evaluations',
      image: '../../../assets/Evaluation-Re-Evluation.png',
      selected_image: '../../../assets/Evaluation-Re-Evluation-selected.png',
      selected: false,
      router_link: '/re-evaluations/list',
      permissions: this.gs.user_data?.role == 'Student',
    },
    {
      id: 11,
      label: 'Activity Logs',
      image: '../../../assets/Masters.png',
      selected_image: '../../../assets/Masters_selected.png',
      selected: false,
      router_link: '/activity-log/list',
      submenu: [],
      permissions: this.gs.user_role_data?.masters_permission,
    },
    {
      id: 10,
      label: 'Payment History',
      image: '../../../assets/Payment-History.png',
      selected_image: '../../../assets/Payment-History-selected.png',
      selected: false,
      router_link: '/transaction-history/list',
      permissions: this.gs.user_data?.role == 'Student',
    },
  ]

  deselect_others(selected_item: any) {
    // Temporary work done by Akash - Asked rudra to remove later.
    this.sidebar.forEach((item: any) => {
      if (item !== selected_item) {
        item.selected = false;
      }
    });
  }

  // track selection state for logout button (used for styling)
  logoutSelected = false;

  async logout() {
    try {
      // mark active for styling before performing logout
      this.logoutSelected = true;
      // let create_response = await this.login.logout({token:this.gs.user_role_data?.token})
      // this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
      localStorage.clear()
      this.router.navigate(['/sign-in']);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    }
  }
}